import { Injectable, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }      from '@ngx-translate/core';

import { ApiService }              from '../api/api.service';
import { ImportColumnModel }       from './typings/imports.column';
import { ImportFormFieldModel }    from './typings/imports.form-field';
import { ImportTransformFunctionModel } from './typings/imports.transform-function';
import { ImportProgressComponent } from './import.progress.component';
import { ImportProposedMappingModel } from './typings/imports.proposed-mapping';
import { ImportHubStateModel }     from './typings/imports.hub';
import { ImportRunSummaryModel }   from './typings/imports.summary';
import { ImportStructureModel }    from './typings/imports.import-structure';
import { JsonService }             from '../core/services/json.service';

@Injectable({
  providedIn: 'root'
})
export class ImportsService implements OnDestroy {
  phoneColumns : Array<string> = [
    'contact_phone',
    'phone',
    'mobile',
    'fax',
    'contact1_phone',
    'contact2_phone'
  ];

  // WS-6751. Choosing the field is enough; the operator should not then have to say the
  // same thing again by picking the matching correction rule. A customer raised exactly
  // this twice in September 2025, once against customer data (WS-5415) and once against
  // vehicle data (WS-5417).
  //
  // Keyed on the import endpoint, because that is already what tells one import from
  // another on this component, then on our field name. Both sides are taken from the
  // backend's own column and action constants rather than guessed. Anything not listed
  // here keeps the existing default, so an import type with no identifier column
  // behaves exactly as it does today.
  identifierActions : { [ url : string ] : { [ ourColumn : string ] : string } } = {
    '/imports/perform_customer_import'   : {
      imported_id: 'treat_as_customer_id'
    },
    '/imports/perform_vendor_import'     : {
      imported_id: 'treat_as_vendor_id'
    },
    '/imports/perform_product_import'    : {
      imported_id       : 'treat_as_product_id',
      imported_vendor_id: 'treat_as_products_vendor_id'
    },
    '/imports/perform_vehicle_import'    : {
      imported_id         : 'treat_as_vehicle_id',
      imported_customer_id: 'treat_as_vehicles_customer_id'
    },
    '/imports/perform_unit_import'       : {
      imported_id: 'treat_as_vehicle_id'
    },
    '/imports/perform_historyh_import'   : {
      imported_id        : 'treat_as_vehicle_history_id',
      imported_vehicle_id: 'treat_as_vehicle_history_vehicle_id'
    },
    '/imports/perform_historyl_import'   : {
      imported_id        : 'treat_as_vehicle_history_item_id',
      imported_history_id: 'treat_as_vehicle_history_header_id'
    },
    '/imports/perform_bomHeader_import'  : {
      imported_id       : 'treat_as_bom_id',
      imported_vendor_id: 'treat_as_products_vendor_id'
    },
    '/imports/perform_bomItems_import'   : {
      imported_id           : 'treat_as_bom_item_id',
      imported_product_id   : 'treat_as_bom_product_id',
      imported_bom_parent_id: 'treat_as_bom_header_id'
    }
  };

  progress: any;
  modal   : NgbModalRef;
  xhr     : XMLHttpRequest

  isImporting = false;

  // WS-6750. A run that exists and is not finished, so opening the screen picks it up
  // rather than showing an idle page next to a live import.
  static readonly IN_FLIGHT_STATUSES = [ 'queued', 'running', 'awaiting_decisions' ];

  // WS-6788. Statuses the run moves on from BY ITSELF, which is not the same question.
  //
  // awaiting_decisions belongs in the list above and not in this one. The run has
  // stopped and is waiting for the operator to answer, so polling it forever is waiting
  // for something that cannot happen without them. It kept the progress modal open over
  // the decision screen, and that modal's only button cancels the import, so the Create
  // and Skip buttons sat behind the backdrop and the only way through was a page reload.
  static readonly WATCHING_STATUSES = [ 'queued', 'running' ];

  // Consecutive unanswered polls before watchRun gives up. Five at three seconds is
  // about fifteen seconds of silence, long enough to ride out a blip and short enough
  // that nobody sits looking at a frozen percentage.
  static readonly MAX_UNANSWERED_POLLS = 5;

  // Poll handle for the run's status, which is separate from the percentage poll. The
  // percentage says how far through the file the worker is; the status says whether it
  // is still going at all, and only the second can tell us it has finished.
  runWatch: any;

  constructor (
    private apiService      : ApiService,
    private jsonService     : JsonService,
    private modalService    : NgbModal,
    private translateService: TranslateService
  ) {}

  cancelImport () : ng.IPromise<any> {
    return this.apiService.get('/imports/cancel_import')
    .then(() => {
      this.stopWatchingRun();

      this.modal.close();

      return this.translateService.instant('JS_SPACE.IMPORTS.IMPORT_CANCELED');
    });
  }

  // WS-6750. Watches the run until it reaches an outcome.
  //
  // Before this the screen held one long request open and treated its reply as the
  // result. On a large file that request timed out at sixty seconds and the operator was
  // shown a failure while the import carried on writing perfectly well for another four
  // or five minutes. They then quite reasonably tried again, and the same records were
  // written twice.
  //
  // Now the submit returns an acknowledgement straight away and the outcome is read from
  // the run itself. Nothing depends on a connection staying open, which is also what
  // makes leaving the screen and coming back work.
  watchRun ( onUpdate : ( summary : ImportRunSummaryModel ) => void )
    : Promise<ImportRunSummaryModel> {
    this.stopWatchingRun();

    // fetchRunSummary turns every failure into null so that a blip cannot break the
    // screen. The cost is that a run that has genuinely stopped answering looks exactly
    // like one that is still going, so without a limit this polls for ever and the
    // operator is left watching a percentage that will never move. Give up after a
    // handful and say so, because a stuck modal is worse than an error.
    let unanswered = 0;

    return new Promise(resolve => {
      const check = () => {
        this.fetchRunSummary().then(summary => {
          if ( !summary ) {
            unanswered = unanswered + 1;

            if ( unanswered < ImportsService.MAX_UNANSWERED_POLLS ) {
              return;
            }

            this.stopWatchingRun();

            resolve(null);

            return;
          }

          unanswered = 0;

          onUpdate(summary);

          if ( ImportsService.WATCHING_STATUSES.includes(summary.status) ) {
            return;
          }

          this.stopWatchingRun();

          resolve(summary);
        });
      };

      // Straight away, then on a timer, so returning to the screen shows the current
      // state rather than a blank few seconds.
      check();

      this.runWatch = setInterval(check, 3000);
    });
  }

  stopWatchingRun () : void {
    if ( this.runWatch ) {
      clearInterval(this.runWatch);

      this.runWatch = null;
    }
  }

  // WS-6750. True when a run is still going, so a screen opening fresh can pick it up
  // rather than showing nothing and letting the operator start a second one.
  //
  // WS-6816 widens it. A run that has FINISHED and still has rows nobody has dealt with
  // is also worth picking up, because the operator has unfinished business with it. Until
  // now only queued, running and awaiting_decisions counted, so an import that completed
  // while the operator was away became unreachable the moment it finished: the screen
  // opened blank, the result was gone, and the rows it could not take were gone with it.
  //
  // That is the case the whole background design exists for. An import runs in the
  // background precisely so it can outlive the screen, and then the screen could not find
  // it again.
  //
  // Deliberately gated on there being groups rather than on the run being recent. A
  // finished run with nothing outstanding should leave the screen clear for the next
  // import; a finished run with rows waiting should not. It bounds itself too: once those
  // rows are cleared away on their retention, groups comes back empty and this stops
  // offering the run, with no date arithmetic and no second source of truth about age.
  runToResume () : Promise<ImportRunSummaryModel> {
    return this.fetchRunSummary().then(summary => {
      if ( !summary ) {
        return null;
      }

      if ( ImportsService.IN_FLIGHT_STATUSES.includes(summary.status) ) {
        return summary;
      }

      if ( summary.status === 'finished' && summary.groups?.length ) {
        return summary;
      }

      return null;
    });
  }

  // Kept so nothing outside this file has to change at once. Asks the narrower question
  // it always asked: is a run still going.
  hasRunInFlight () : Promise<ImportRunSummaryModel> {
    return this.fetchRunSummary().then(summary => {
      if ( summary && ImportsService.IN_FLIGHT_STATUSES.includes(summary.status) ) {
        return summary;
      }

      return null;
    });
  }

  isPhoneColumn ( columnName : string ) : boolean {
    return this.phoneColumns.includes(columnName);
  }

  // WS-6751. The correction rule that a field implies, or null when it implies none.
  identifierActionFor ( url : string, ourColumn : string ) : string {
    if ( !url || !ourColumn ) {
      return null;
    }

    return ( this.identifierActions[ url ] || {} )[ ourColumn ] || null;
  }

  // WS-6753. What each type has already had brought across, for the whole company.
  // Company-wide because onboarding spans days and people, so a per user view would
  // show a colleague's finished work as still to do.
  fetchHubState () : Promise<Array<ImportHubStateModel>> {
    return this.apiService.get('/imports/import_hub_state')
    .then(( state : Array<ImportHubStateModel> ) => state || [])
    .catch(() => []);
  }

  // WS-6819. The rows the import could not take, as a file to correct and import again.
  //
  // Fetched directly rather than through apiService, which parses every response as JSON
  // and would mangle a CSV. credentials: 'include' is what the application's own calls
  // already do, so the session travels and the endpoint scopes the rows to that business.
  //
  // The filename comes from the response so the operator can tell which import a file in
  // their downloads folder belongs to. Falls back to a fixed name rather than failing,
  // because a download with a dull name beats no download.
  fetchHeldRows () : Promise<{ blob : Blob, filename : string }> {
    const url = `${ this.apiService.getEndpoint() }/imports/held_rows`;

    return fetch(url, { credentials: 'include' })
    .then(response => {
      if ( !response.ok ) {
        return Promise.reject(response.status);
      }

      const disposition = response.headers.get('content-disposition') || '';
      const match       = disposition.match(/filename="?([^";]+)"?/i);

      return response.blob().then(blob => ({
        blob    : blob,
        filename: match ? match[1] : 'rows-to-correct.csv'
      }));
    });
  }

  // The fields this import type can map onto, and the corrections it can apply.
  //
  // These are per import type and they come from the backend. The eleven AngularJS
  // controllers the rebuild deleted each fetched both, which is how the mapping screen
  // was ever able to offer a field to map to. Nothing in the rebuild fetched them, so
  // every dropdown on the mapping screen came back empty and no import could be
  // completed at all. Both endpoints were live the whole time.
  //
  // An empty list is returned on failure rather than an error. The mapping screen is
  // useless without them, but failing loudly here would take down the hub as well, and
  // the screen already tells the operator when it has nothing to offer.
  fetchOurColumns ( forTable : string ) : Promise<Array<ImportColumnModel>> {
    if ( !forTable ) {
      return Promise.resolve([]);
    }

    return this.apiService.get('/imports/our_column_details', forTable)
    .then(( columns : Array<ImportColumnModel> ) => columns || [])
    .catch(() => []);
  }

  fetchTransformFunctions ( forTable : string )
    : Promise<Array<ImportTransformFunctionModel>> {
    if ( !forTable ) {
      return Promise.resolve([]);
    }

    return this.apiService.get('/imports/available_actions', forTable)
    .then(( functions : Array<ImportTransformFunctionModel> ) => functions || [])
    .catch(() => []);
  }

  // WS-6752. What the check found, so the operator can see what is about to happen
  // before any of it happens. Read-only, and safe to call at any point: a business with
  // no import returns a null status rather than an error.
  fetchRunSummary () : Promise<ImportRunSummaryModel> {
    return this.apiService.get('/imports/import_groups')
    .then(( summary : ImportRunSummaryModel ) => summary || null)
    .catch(() => null);
  }

  // WS-6752. Sends create or skip per group and lets the run carry on. Keyed on the
  // group's reference_type, which is what the backend answers against. Only groups that
  // can be created are ever sent, because the rest have nothing to answer.
  applyImportDecisions ( decisions : { [ referenceType : string ] : string } )
    : Promise<any> {
    return this.apiService.post('/imports/apply_import_decisions', {
      decisions: decisions
    });
  }

  // WS-6751. Fetches a proposed mapping when the import has somewhere to fetch one
  // from. Nothing sets proposalUrl today, so nothing is requested and every import maps
  // by hand exactly as it does now. WS-6749 supplies both the endpoint and the value.
  //
  // Resolves to null on any failure by design. A proposal is a convenience, and an
  // import must never be blocked because we could not offer one.
  //
  // forTable and rows are both required by the endpoint. Without forTable it has no
  // field list to map onto, and without rows every column is described as empty, so a
  // call missing either produces a proposal that is uniformly useless rather than an
  // error anybody would notice.
  fetchProposedMapping ( proposalUrl : string, forTable : string,
                         headings : Array<string>, rows : Array<Array<string>> )
    : Promise<ImportProposedMappingModel> {
    if ( !proposalUrl || !forTable ) {
      return Promise.resolve(null);
    }

    return this.apiService.post(proposalUrl, {
      for_table: forTable,
      headings : headings,
      rows     : rows
    })
    .then(( proposal : ImportProposedMappingModel ) => {
      // WS-6964. Anything without columns used to collapse to null here, which threw
      // away the difference between an answer with nothing in it and no answer at all.
      // The screen needs that difference to tell the operator the truth, so the reply is
      // passed through as it arrived and the component decides what to say about it.
      return proposal || null;
    })
    .catch(() => {
      // The request itself did not come back. That is a failure whatever a body would
      // have said, and it is the case that was previously reported as "no suggestions".
      return { columns: [], outcome: 'failed' } as ImportProposedMappingModel;
    });
  }

  ngOnDestroy () : void {
    this.stopProgress();

    if ( this.xhr ) {
      this.xhr.removeAllListeners('abort');
      this.xhr.removeAllListeners('error');
      this.xhr.removeAllListeners('loadend');
    }
  }

  showProgress () : ng.IPromise<any> {
    this.modal = this.modalService.open(ImportProgressComponent);

    this.modal.componentInstance.percentage = 0;

    this.modal.closed.subscribe(() => {
      this.stopProgress();
    });

    this.modal.dismissed.subscribe(() => {
      this.stopProgress();
    });

    this.modal.shown.subscribe(() => {
      this.isImporting = true;
    });

    return this.modal.result;
  }

  start ( importUrl : string, file : File, importStructure : Array<ImportStructureModel>, hasHeaders = true, additionalFormFields : Array<ImportFormFieldModel> = [] ) : ng.IPromise<any> {
    return new Promise(( resolve, reject ) => {
      this.showProgress()
      .catch(() => resolve(this.cancelImport()));

      const stringedStructure = JSON.stringify(importStructure);

      const form = new FormData;

      // Specify whether there are column names included
      // in the first row so that row can be skipped if needed
      form.append('has_headers', hasHeaders.toString());

      // Pass the stringed structure
      form.append('import_structure', stringedStructure);

      // Now finally the file
      form.append('csv_file', file);

      if ( additionalFormFields.length ) {
        additionalFormFields.forEach(additionalFormField => {
          form.append(additionalFormField.name, additionalFormField.value);
        });
      }

      this.xhr = new XMLHttpRequest;

      this.xhr.addEventListener('abort', ( e ) => {
        this.modal.close();

        reject(this.translateService.instant('JS_SPACE.IMPORTS.FAILED'));
      });

      this.xhr.addEventListener('error', ( e ) => {
        this.modal.close();

        reject(this.translateService.instant('JS_SPACE.IMPORTS.FAILED'));
      });

      this.xhr.addEventListener('loadend', ( e : any ) => {
        if ( e.target.status !== 200 ) {
          this.modal.close();

          reject(this.jsonService.parse(e.target.responseText));

          return;
        }

        // WS-6750. A 200 here means the import was accepted, not that it finished. The
        // modal deliberately stays open: closing it now is what lost the progress
        // display the moment the backend stopped blocking.
        this.updateStatus('running');

        this.watchRun(summary => this.updateStatus(summary.status))
        .then(summary => {
          this.modal.close();

          // Null means the run stopped answering, not that it failed. It may well still
          // be running, so say that rather than reporting an outcome we do not have.
          if ( !summary ) {
            reject(this.translateService.instant('JS_SPACE.IMPORTS.LOST_CONTACT'));

            return;
          }

          resolve(summary);
        });
      });

      // Start the call
      this.xhr.open('POST', `${ this.apiService.getEndpoint() }${ importUrl }`, true);

      this.xhr.withCredentials = true;

      // Send the formdata
      this.xhr.send(form);

      this.progress = setInterval(() => {
        this.apiService.get('/imports/import_progress')
        .then(progress => this.updateProgress(+( parseFloat(progress) * 100 ).toFixed(1)));
      }, 2000);
    });
  }

  // WS-6807. A run told to carry on after the operator answered needs the same progress
  // display a fresh import gets. start() builds that around the upload, and there is no
  // upload here: the file has been on the bucket since the run was accepted and the
  // worker is only being told to resume. So this is the two parts of start() that are
  // not the request, and nothing else.
  //
  // Without it the operator answered, the screen went blank, and the only thing left on
  // it said the import was safe to leave. That is the worst possible moment to say so:
  // the whole design depends on them still being there when it finishes, and they stay
  // because they can see it working.
  resumeProgress () : ng.IPromise<any> {
    const shown = this.showProgress();

    this.updateStatus('running');

    this.progress = setInterval(() => {
      this.apiService.get('/imports/import_progress')
      .then(progress => this.updateProgress(+( parseFloat(progress) * 100 ).toFixed(1)));
    }, 2000);

    return shown;
  }

  // The other half of resumeProgress. start() closes the modal itself when its own watch
  // resolves; a resumed run is watched by the screen instead, so it has to be able to
  // put the modal away when that watch is done.
  closeProgress () : void {
    this.stopProgress();

    if ( this.modal ) {
      this.modal.close();
    }
  }

  // Tells the open modal what the run is doing, so it can say "running" rather than
  // showing a percentage with no context.
  updateStatus ( status : string ) : void {
    if ( this.modal?.componentInstance ) {
      this.modal.componentInstance.status = status;
    }
  }

  stopProgress () : void {
    this.isImporting = false;

    this.stopWatchingRun();

    if ( this.progress ) {
      clearInterval(this.progress);
    }
  }

  updateProgress ( percentage : number ) : void {
    if ( this.isImporting ) {
      this.modal.componentInstance.percentage = percentage;
    }
  }
}
